import { eventKeys } from "@/lib/eventKeys";
import { isOriginReachable } from "@/lib/networkReachability";
import { FrontendError } from "@cs_teamwaydev/mailvista-sdk_react";
import { useCallback, useEffect, useRef, useState } from "react";
import useGetEventBus from "./useGetEventBus";

type StartupFailure = "none" | "generic" | "b2c-not-allowed";

const REACHABILITY_RETRY_MS = 2_000;

/**
 * Sans ce délai : six rechargements enchaînés en 41 s, terminés sur une page blanche. Doit
 * rester plus long qu'un démarrage complet (~10 s). Pendant mobile : `RESTART_GRACE_MS`.
 */
const RELOAD_GRACE_MS = 30_000;

/** En session et non en module : un rechargement réinitialise tout l'état JavaScript. */
const LAST_RELOAD_KEY = "mailvista.lastAutoReloadAt";

const hasReloadedRecently = (): boolean => {
    try {
        const raw = sessionStorage.getItem(LAST_RELOAD_KEY);

        return raw !== null && Date.now() - Number(raw) < RELOAD_GRACE_MS;
    } catch {
        return false;
    }
};

const rememberReload = (): void => {
    try {
        sessionStorage.setItem(LAST_RELOAD_KEY, String(Date.now()));
    } catch {
        // Stockage indisponible : on perd le garde-fou, pas le rechargement.
    }
};

const useServiceErrorListener = () => {
    const [failure, setFailure] = useState<StartupFailure>("none");
    const failureRef = useRef<StartupFailure>("none");
    const isConfirmingRef = useRef(false);
    const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isUnmountedRef = useRef(false);

    const setFailureState = useCallback((value: StartupFailure) => {
        failureRef.current = value;
        setFailure(value);
    }, []);

    const handleStartupFailed = useCallback(
        (error: FrontendError) => {
            setFailureState(
                error === FrontendError.USER_B2C_NOT_ALLOWED
                    ? "b2c-not-allowed"
                    : "generic",
            );
        },
        [setFailureState],
    );

    const handleNetworkThresholdReached = useCallback(() => {
        setFailureState("generic");
    }, [setFailureState]);

    /**
     * Boucle sans plafond : recharger avant que l'origine réponde produirait la page blanche
     * qu'on évite, et `network-online` n'est émis qu'à la transition — nul ne relancerait.
     */
    const reloadWhenReachable = useCallback(async () => {
        // Rabaissé sur toute sortie qui ne recharge pas : sinon un abandon fige la boucle.
        if (isConfirmingRef.current || isUnmountedRef.current) {
            return;
        }
        isConfirmingRef.current = true;

        const attempt = async (): Promise<void> => {
            if (isUnmountedRef.current) {
                isConfirmingRef.current = false;

                return;
            }

            let reachable = false;

            try {
                reachable = await isOriginReachable();
            } catch {
                reachable = false;
            }

            // Relu après l'await : un minuteur posé après démontage échapperait au nettoyage.
            if (isUnmountedRef.current) {
                isConfirmingRef.current = false;

                return;
            }

            if (reachable && !hasReloadedRecently()) {
                rememberReload();
                window.location.reload();

                return;
            }

            retryTimerRef.current = setTimeout(() => {
                void attempt();
            }, REACHABILITY_RETRY_MS);
        };

        await attempt();
    }, []);

    const handleNetworkOnline = useCallback(() => {
        if (failureRef.current === "generic") {
            void reloadWhenReachable();
        }
    }, [reloadWhenReachable]);

    const { off: offStartupFailed } = useGetEventBus<FrontendError>(
        eventKeys.startup.failed,
        handleStartupFailed,
    );
    const { off: offOfflineThreshold } = useGetEventBus(
        eventKeys.network.offlineThreshold,
        handleNetworkThresholdReached,
    );
    const { off: offNetworkOnline } = useGetEventBus(
        eventKeys.network.online,
        handleNetworkOnline,
    );

    useEffect(() => {
        // StrictMode enchaîne montage → nettoyage → montage sur la même fibre : sans cette
        // remise à false, la boucle ne partirait jamais en développement.
        isUnmountedRef.current = false;

        return () => {
            offStartupFailed(eventKeys.startup.failed, handleStartupFailed);
            offOfflineThreshold(
                eventKeys.network.offlineThreshold,
                handleNetworkThresholdReached,
            );
            offNetworkOnline(eventKeys.network.online, handleNetworkOnline);

            isUnmountedRef.current = true;
            if (retryTimerRef.current) {
                clearTimeout(retryTimerRef.current);
            }
        };
    }, []);

    return {
        hasError: failure === "generic",
        isB2CNotAllowed: failure === "b2c-not-allowed",
    };
};

export default useServiceErrorListener;
